import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to create or update pickup service configuration for a services provider
 */
export const createPickupService = async (
	servicesProviderId: string,
	data: Record<string, unknown>,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.pickup(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for creating or updating pickup service configuration for a services provider
 */
export const useUpdatePickupService = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: Record<string, unknown>) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create pickup service without a services provider ID.',
				);
			}
			return createPickupService(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
